import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import * as React from 'react';
import { useWindowWidthDetection } from '~/Utils/Hooks/useWindowWidthDetection';
import { MediaBreakpoints } from '~/Utils/MediaBreakpoints';
import styles from './ComboSelector.module.css';
import type { ComboSelectorColorVariant } from './ComboSelectorColorVariant';
import type { ComboSelectorVariant } from './ComboSelectorVariant';
import { ComboSelectorButtonGroup } from './Components/ComboSelectorButtonGroup/ComboSelectorButtonGroup';
import { ComboSelectorDropdown } from './Components/ComboSelectorDropdown/ComboSelectorDropdown';
import { ComboSelectorRadioGroup } from './Components/ComboSelectorRadioGroup/ComboSelectorRadioGroup';

export interface ComboSelectorProps {
    name: string;
    options: Map<string, string>;
    onChange: (selectionId: string) => void;
    selectSwitchCallback?: (size: number) => boolean;
    variant?: ComboSelectorVariant;
    colorVariant?: ComboSelectorColorVariant;
    label?: string;
    currentValue?: string;
    className?: string;
}

export const ComboSelector: React.FC<ComboSelectorProps> = props => {
    const {
        name,
        onChange,
        label,
        className,
        options,
        currentValue,
        selectSwitchCallback = size => size >= 4,
        colorVariant = 'light',
        variant = 'radio',
    } = props;

    const isMobile = useWindowWidthDetection({ condition: 'lessThan', breakpoint: MediaBreakpoints.Tablet });
    const labelId = uniqueId('comboSelect_id');

    const partialsPropsBase = {
        options,
        colorVariant,
        labelId,
        onChange,
        currentValue,
    };

    const renderContent = () => {
        const shouldRenderSelect = selectSwitchCallback(options.size) || isMobile;

        if (shouldRenderSelect) {
            return <ComboSelectorDropdown {...partialsPropsBase} />;
        }

        if (variant === 'radio') {
            return <ComboSelectorRadioGroup {...partialsPropsBase} name={name} />;
        }

        return <ComboSelectorButtonGroup {...partialsPropsBase} />;
    };

    const comboSelectorColorVariant = colorVariant === 'greySelectDarkButtons' ? undefined : colorVariant;

    return (
        <div
            className={classNames(
                styles.ComboSelector,
                comboSelectorColorVariant && styles[`Variant--${comboSelectorColorVariant}`],
                className
            )}
        >
            {label && (
                <div id={labelId} className={styles.Label}>
                    {label}
                </div>
            )}
            {renderContent()}
        </div>
    );
};
